import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Pause, Play, VideoOff } from "lucide-react";
import "./videoDetails.scss";
import { fetchVideo } from "../../api/newsService";
import { TitleComponent } from "../TitleDocument";
import { getVideoDateLabel } from "./videoUtils";
import BreadCrumb from "../BreadCrumb";
import RelatedMediaSidebar from "../RelatedMediaSidebar/RelatedMediaSidebar";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");

  return `${minutes}:${remainingSeconds}`;
};

const VideoDetailLoading = () => (
  <section className="video_detail">
    <TitleComponent title="Chi tiết video" />

    <div className="video_detail__crumb container">
      <BreadCrumb />
    </div>

    <div className="video_detail__layout container">
      <article className="video_detail__main">
        <div className="video_detail__loading-card" role="status" aria-live="polite">
          <div className="video_detail__loading-screen" />
          <div className="video_detail__loading-controls" />
          <div className="video_detail__loading-copy">
            <span />
            <span />
            <p>Đang tải nội dung...</p>
          </div>
        </div>
      </article>

      <aside className="video_detail__loading-sidebar" aria-hidden="true">
        <div className="video_detail__loading-sidebar-head" />
        {[1, 2, 3, 4, 5].map((item) => (
          <div className="video_detail__loading-sidebar-item" key={item}>
            <span />
            <div>
              <i />
              <i />
            </div>
          </div>
        ))}
      </aside>
    </div>
  </section>
);

const VideoDetails = () => {
  const { newId } = useParams();
  const videoRef = useRef(null);
  const [postVideo, setPostVideo] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const getVideo = async () => {
      try {
        const res = await fetchVideo();
        if (isMounted && res?.data?.data?.lstVideo?.length > 0) {
          setPostVideo(res.data.data.lstVideo);
        } else if (isMounted) {
          setPostVideo([]);
        }
      } catch (error) {
        if (isMounted) {
          setPostVideo([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    getVideo();

    return () => {
      isMounted = false;
    };
  }, []);

  const videos = postVideo;

  const currentVideo = useMemo(() => {
    const detail = videos.find((item) => Number(item.id) === Number(newId));
    return detail || null;
  }, [newId, videos]);

  const otherVideos = useMemo(
    () => (currentVideo ? videos.filter((item) => Number(item.id) !== Number(currentVideo.id)) : []),
    [currentVideo?.id, videos]
  );

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    videoElement.pause();
    videoElement.load();
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [currentVideo?.id]);

  const handleTogglePlay = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (videoElement.paused) {
      const playPromise = videoElement.play();

      if (playPromise?.then) {
        playPromise.then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
      } else {
        setIsPlaying(true);
      }

      return;
    }

    videoElement.pause();
    setIsPlaying(false);
  };

  const handleProgressChange = (event) => {
    const videoElement = videoRef.current;
    if (!videoElement || !duration) return;

    const nextProgress = Number(event.target.value);
    const nextTime = (nextProgress / 100) * duration;
    videoElement.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const progress = duration ? Math.min((currentTime / duration) * 100, 100) : 0;
  const durationLabel = duration ? formatTime(duration) : "--:--";
  const dateLabel = getVideoDateLabel(currentVideo);

  if (isLoading) {
    return <VideoDetailLoading />;
  }

  if (!currentVideo) {
    return (
      <section className="video_detail">
        <TitleComponent title="Chi tiết video" />

        <div className="video_detail__crumb container">
          <BreadCrumb />
        </div>

        <div className="video_detail__empty container">
          <span className="video_detail__empty-icon">
            <VideoOff size={44} />
          </span>
          <h1>Chưa có video được đăng tải</h1>
          <p>Video mới sẽ được cập nhật khi có dữ liệu từ hệ thống.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="video_detail">
      <TitleComponent title={currentVideo?.title} />

      <div className="video_detail__crumb container">
        <BreadCrumb />
      </div>

      <div className="video_detail__layout container">
        <article className="video_detail__main">
          <div className="video_detail__player-card">
            <div className="video_detail__screen">
              <video
                ref={videoRef}
                key={currentVideo?.id}
                poster={currentVideo?.image}
                preload="metadata"
                onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
                onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              >
                <source src={currentVideo?.videoPath} type="video/mp4" />
              </video>

              <button
                type="button"
                className="video_detail__screen-play"
                onClick={handleTogglePlay}
                aria-label={isPlaying ? "Tạm dừng video" : "Phát video"}
              >
                {isPlaying ? <Pause size={34} /> : <Play size={34} fill="currentColor" />}
              </button>
            </div>

            <div className="video_detail__controls">
              <button
                type="button"
                className="video_detail__play-button"
                onClick={handleTogglePlay}
                aria-label={isPlaying ? "Tạm dừng video" : "Phát video"}
              >
                {isPlaying ? <Pause size={22} /> : <Play size={22} fill="currentColor" />}
              </button>

              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={handleProgressChange}
                className="video_detail__progress"
                style={{ "--progress": `${progress}%` }}
                aria-label="Tiến trình video"
              />

              <span className="video_detail__time">
                {formatTime(currentTime)} / {durationLabel}
              </span>
            </div>

            <div className="video_detail__info">
              <h1>{currentVideo?.title}</h1>
              {dateLabel && (
                <div className="video_detail__date">
                  <CalendarDays size={15} />
                  <span>{dateLabel}</span>
                </div>
              )}
            </div>
          </div>
        </article>

        <RelatedMediaSidebar
          title="Video khác"
          viewAllPath="/thu-vien-video"
          items={otherVideos}
          variant="video"
          getItemPath={(video) => `/thu-vien-video/${video.id}`}
          getDateLabel={getVideoDateLabel}
        />
      </div>
    </section>
  );
};

export default VideoDetails;
